import React, { useState, useEffect } from 'react';
import { useLightboxImageState } from 'hooks';

/**
 * Modal component for displaying photos in a lightbox-style interface
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Array} props.photos - Array of photo objects with id, url, thumb_url
 * @param {number} props.currentIndex - Index of currently displayed photo
 * @param {Function} props.onClose - Function to close modal
 * @param {Function} props.onOverlayClick - Function to handle overlay clicks
 * @param {React.RefObject} props.modalRef - Ref for focus management
 * @param {string} props.locale - Current locale for translations
 */
const PhotoModal = ({
  isOpen,
  photos = [],
  currentIndex = 0,
  onClose,
  onOverlayClick,
  modalRef,
  translations = {}
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(currentIndex);
  const {
    isLoading: isImageLoading,
    hasError: imageError,
    imgRef,
    onLoad: handleImageLoad,
    onError: handleImageError
  } = useLightboxImageState(`${isOpen}-${currentPhotoIndex}`);

  // Get translation text
  const getTranslation = (key, replacements = {}) => {
    let text = translations[key] || '';
    
    // Replace placeholders
    Object.keys(replacements).forEach(placeholder => {
      text = text.replace(`%{${placeholder}}`, replacements[placeholder]);
    });
    
    return text;
  };

  // Sync the displayed index when the modal opens or the photo set changes.
  // Loading/error UI is handled by useLightboxImageState (keyed on the index).
  useEffect(() => {
    if (isOpen && photos.length > 0) {
      setCurrentPhotoIndex(Math.max(0, Math.min(currentIndex, photos.length - 1)));
    }
  }, [isOpen, photos, currentIndex]);

  // Prevent browser default arrow key navigation when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const preventArrowKeys = (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add event listener to document with capture to intercept early
    document.addEventListener('keydown', preventArrowKeys, { capture: true });

    return () => {
      document.removeEventListener('keydown', preventArrowKeys, { capture: true });
    };
  }, [isOpen]);

  const goToPrevious = () => {
    if (photos.length === 0) return;
    const newIndex = currentPhotoIndex === 0 ? photos.length - 1 : currentPhotoIndex - 1;
    setCurrentPhotoIndex(newIndex);
  };

  const goToNext = () => {
    if (photos.length === 0) return;
    const newIndex = currentPhotoIndex === photos.length - 1 ? 0 : currentPhotoIndex + 1;
    setCurrentPhotoIndex(newIndex);
  };

  if (!isOpen || photos.length === 0) {
    return null;
  }

  const currentPhoto = photos[currentPhotoIndex];
  const hasMultiplePhotos = photos.length > 1;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/90 p-4"
      onClick={onOverlayClick}
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-modal-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Modal Content */}
      <div
        className="relative w-full h-full max-w-6xl max-h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors pointer-events-auto"
          aria-label={getTranslation('modal_close')}
          style={{ zIndex: 9999 }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Previous Button */}
        {hasMultiplePhotos && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors pointer-events-auto"
            aria-label={getTranslation('modal_previous')}
            style={{ zIndex: 9999 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Next Button */}
        {hasMultiplePhotos && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors pointer-events-auto"
            aria-label={getTranslation('modal_next')}
            style={{ zIndex: 9999 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Photo Counter */}
        {hasMultiplePhotos && (
          <div className="absolute top-4 left-4 z-10 px-3 py-2 bg-black/50 text-white rounded-full text-sm">
            {getTranslation('modal_photo_of', { current: currentPhotoIndex + 1, total: photos.length })}
          </div>
        )}

        {/* Main Image Container */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Loading State */}
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-white">{getTranslation('modal_loading')}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-white">{getTranslation('modal_load_error')}</p>
              </div>
            </div>
          )}

          {/* Photo */}
          {currentPhoto && (
            <img
              ref={imgRef}
              src={currentPhoto.url}
              alt={`Teaching photo ${currentPhotoIndex + 1}`}
              className={`max-w-full max-h-full object-contain transition-opacity duration-200 ${
                isImageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: imageError ? 'none' : 'block' }}
            />
          )}
        </div>

        {/* Photo Dots Navigation */}
        {hasMultiplePhotos && photos.length <= 10 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {photos.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPhotoIndex(index);
                }}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentPhotoIndex
                    ? 'bg-white'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoModal;